import React, { createContext, useState, useEffect, useCallback } from 'react';
import { listStores, getMyStoreRoles } from './utils/queries';

const MyContext = createContext();

const MyContextProvider = ({ children }) => {
	const [errorMsg, setErrorMsg] = useState('');

	// Stores this admin can switch between - loaded once after login, and
	// the active selection persisted so it survives reloads/tabs.
	const [stores, setStores] = useState([]);
	const [activeStoreId, setActiveStoreIdState] = useState(() => {
		const saved = localStorage.getItem('activeStoreId');
		return saved ? Number(saved) : null;
	});

	const setActiveStoreId = (storeId) => {
		localStorage.setItem('activeStoreId', String(storeId));
		setActiveStoreIdState(storeId);
	};

	const refreshStores = useCallback(async () => {
		// No limit/offset -- the store-picker dropdown used across the app
		// needs every store, never a paginated page (see stores.service.ts's
		// listStores, which omits pagination entirely when called this way).
		const result = await listStores();
		if (!result?.success) return;
		const allStores = result.data.stores;
		setStores(allStores);
		setActiveStoreIdState((current) => {
			if (current && allStores.some((s) => s.storeId === current)) return current;
			const first = allStores[0]?.storeId ?? null;
			if (first) localStorage.setItem('activeStoreId', String(first));
			return first;
		});
	}, []);

	// {[storeId]: role} for the logged-in user -- role is per-store now, so
	// nav/route gating derives an "effective role" from whichever store is
	// active (see RoleRoute.js, permissions.js, navigation.js) instead of a
	// single flat value. Empty for a PLATFORMADMIN (they have no
	// user_store_access rows; userProfile.role === 'PLATFORMADMIN' is their
	// bypass everywhere this is read). Doesn't need to re-fetch when
	// activeStoreId changes -- it's a map of every store's role, not just
	// the active one.
	const [storeRoles, setStoreRoles] = useState({});
	// {[storeId]: isOrderApprover} -- powers the Approvals nav-link gating
	// (Navigation/UserAvatar). NOTE: /admin/me/store-roles (utils/auth.ts's
	// myStoreRoles) does not yet select/return isOrderApprover -- it's read
	// here defensively (`r.isOrderApprover`) so this map is ready the moment
	// the backend adds it, but until then every store resolves to false and
	// the Approvals links stay hidden for everyone.
	const [approverByStoreId, setApproverByStoreId] = useState({});
	const refreshStoreRoles = useCallback(async () => {
		const result = await getMyStoreRoles();
		if (!result?.success) return;
		const map = {};
		const approverMap = {};
		(result.data.storeRoles || []).forEach((r) => {
			map[r.storeId] = r.role;
			approverMap[r.storeId] = Boolean(r.isOrderApprover);
		});
		setStoreRoles(map);
		setApproverByStoreId(approverMap);
	}, []);
	const activeStoreRole = storeRoles[activeStoreId];

	useEffect(() => {
		if (localStorage.getItem('authToken')) {
			refreshStores();
			refreshStoreRoles();
		}
	}, [refreshStores, refreshStoreRoles]);

	// user profile - stored in localStorage for persistence across sessions
	const getUserProfile = () => {
		const profile = localStorage.getItem('userProfile');
		try {
			return profile ? JSON.parse(profile) : null;
		} catch (error) {
			console.error('Error parsing user profile from localStorage:', error);
			return null;
		}
	};
	const [userProfile, setUserProfile] = useState(getUserProfile);

	// A PLATFORMADMIN has no user_store_access row (see myStoreRoles' own
	// comment) but is always allowed to decide approvals server-side
	// (orderApprovals.service.ts#assertOrderApprovalPermission), so they're
	// treated as an approver for nav-visibility purposes too.
	const activeStoreIsOrderApprover =
		userProfile?.role === 'PLATFORMADMIN' || Boolean(approverByStoreId[activeStoreId]);

	// Sync userProfile state with localStorage changes
	useEffect(() => {
		const handleStorageChange = () => {
			setUserProfile(getUserProfile());
		};

		// Listen for storage changes from other tabs/windows
		window.addEventListener('storage', handleStorageChange);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
		};
	}, []);

	// Update user profile and sync to localStorage
	const updateUserProfile = (profile) => {
		if (profile) {
			localStorage.setItem('userProfile', JSON.stringify(profile));
		} else {
			localStorage.removeItem('userProfile');
		}
		setUserProfile(profile);
	};

	// Clear user profile when component unmounts or on logout
	const clearUserProfile = () => {
		localStorage.removeItem('userProfile');
		setUserProfile(null);
	};

	return (
		<MyContext.Provider
			value={{
				errorMsg,
				setErrorMsg,
				userProfile,
				setUserProfile,
				updateUserProfile,
				clearUserProfile,
				stores,
				activeStoreId,
				setActiveStoreId,
				refreshStores,
				storeRoles,
				activeStoreRole,
				refreshStoreRoles,
				approverByStoreId,
				activeStoreIsOrderApprover,
			}}
		>
			{children}
		</MyContext.Provider>
	);
};

export { MyContext, MyContextProvider };
